import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import type { CareRequirement } from "@caredna-marlin/utils/client";
import CareRequirementFormRow from "./CareRequirementFormRow";

type Props = {
  careRequirements: CareRequirement[];
  setCareRequirements: (updater: (prev: CareRequirement[]) => CareRequirement[]) => void;
  serviceLineId: number;
  timezone?: string;
  createNewRequirement: () => CareRequirement;
  duplicateRequirement: (requirement: CareRequirement, index: number, all: CareRequirement[]) => CareRequirement;
  summaryLabel: (careRequirement: CareRequirement, index: number) => React.ReactNode;
  addButtonLabel?: string;
  emptyLabel?: string;
  addDisabled?: boolean;
  mode?: "openNewRows" | "openLastAdded";
  contentPaddingX?: number;
  disallowRelativeToLastForFirstRow?: boolean;
};

export default function CareRequirementsEditor({
  careRequirements,
  setCareRequirements,
  serviceLineId,
  timezone,
  createNewRequirement,
  duplicateRequirement,
  summaryLabel,
  addButtonLabel = "+ Add Visit",
  emptyLabel = "",
  addDisabled = false,
  mode = "openLastAdded",
  contentPaddingX = 0,
  disallowRelativeToLastForFirstRow = false,
}: Props) {
  const [lastAddedRowIndex, setLastAddedRowIndex] = useState<number | null>(null);
  const [rowActionsMenu, setRowActionsMenu] = useState<null | { index: number; anchorEl: HTMLElement }>(null);

  useEffect(() => {
    if (lastAddedRowIndex === null) { return; }
    if (lastAddedRowIndex >= careRequirements.length) {
      setLastAddedRowIndex(null);
    }
  }, [lastAddedRowIndex, careRequirements.length]);

  const closeRowActionsMenu = () => setRowActionsMenu(null);

  const deleteRow = (index: number) => {
    if (index < 0) { return; }
    setCareRequirements((prev) => prev.filter((_, i) => i !== index));
  };

  const duplicateRow = (index: number) => {
    if (index < 0) { return; }
    setCareRequirements((prev) => {
      const source = prev[index];
      if (!source) { return prev; }
      const duplicate = duplicateRequirement(source, index, prev);
      return [...prev, duplicate];
    });
    // Track the added row index outside the updater to avoid
    // side-effects inside state-transition callbacks.
    setLastAddedRowIndex(careRequirements.length);
  };

  const onAddRow = () => {
    setCareRequirements((prev) => [...prev, createNewRequirement()]);
    setLastAddedRowIndex(careRequirements.length);
  };

  return (
    <>
      {careRequirements.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", py: 1 }}>
          {emptyLabel}
        </Typography>
      ) : (
        <Box>
          {careRequirements.map((careRequirement, index) => (
            <Accordion
              key={careRequirement.id ?? index}
              disableGutters
              square
              sx={{ width: "100%", mb: 1 }}
              defaultExpanded={mode === "openNewRows" ? !careRequirement.id : index === lastAddedRowIndex}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: contentPaddingX,
                  minHeight: 44,
                  "&.Mui-expanded": { minHeight: 44 },
                  "& .MuiAccordionSummary-content.Mui-expanded": { my: 0 },
                  "& .MuiAccordionSummary-expandIconWrapper": { order: -1, mr: 1 },
                  "& .MuiAccordionSummary-content": { alignItems: "center", my: 0 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                  <Box component="span">{summaryLabel(careRequirement, index)}</Box>
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton
                    component="span"
                    size="small"
                    aria-label="Row actions"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRowActionsMenu({ index, anchorEl: e.currentTarget });
                    }}
                    onFocus={(e) => e.stopPropagation()}
                  >
                    <MoreHorizIcon fontSize="small" />
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: contentPaddingX, pt: 0 }}>
                <CareRequirementFormRow
                  careRequirement={careRequirement}
                  setCareRequirement={(nextCareReq) => {
                    setCareRequirements((prev) => prev.map((item, i) => i === index ? nextCareReq : item));
                  }}
                  serviceLineId={serviceLineId}
                  timezone={timezone}
                  isFirstVisitOfDay={index === 0}
                  disallowRelativeSchedule={index === 0}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      <Button onClick={onAddRow} variant="outlined" fullWidth sx={{ mt: 1 }} disabled={addDisabled}>
        {addButtonLabel}
      </Button>

      <Menu
        open={!!rowActionsMenu}
        onClose={closeRowActionsMenu}
        anchorEl={rowActionsMenu?.anchorEl}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            if (!rowActionsMenu) { return; }
            duplicateRow(rowActionsMenu.index);
            closeRowActionsMenu();
          }}
        >
          <ListItemIcon>
            <ContentCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!rowActionsMenu) { return; }
            deleteRow(rowActionsMenu.index);
            closeRowActionsMenu();
          }}
        >
          <ListItemIcon>
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

